import React, { useState, useEffect } from 'react';
import { Check, ArrowRight, Sparkles, Loader2, X, Mail, Copy, Globe, MapPin } from 'lucide-react';
import DraggablePopularTag from './DraggablePopularTag';
import { useLanguage, translations } from '../i18nContext';
import {
  purchaseMembership,
  confirmMembershipPurchase,
  getMembershipInfo,
  getPaymentRegion,
  PaymentRegionInfo,
} from '../services/membership';
import { MembershipInfo } from '../types';
import { copyToClipboard } from '../utils/clipboard';

interface WelcomePricingModalProps {
  onClose: () => void;
}

export default function WelcomePricingModal({ onClose }: WelcomePricingModalProps) {
  const [isAnnual, setIsAnnual] = useState(true);
  const { t, language } = useLanguage();

  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const [purchaseSuccess, setPurchaseSuccess] = useState<string | null>(null);
  const [membershipInfo, setMembershipInfo] = useState<MembershipInfo | null>(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [copied, setCopied] = useState(false);

  // 地区检测弹窗状态
  const [showRegionModal, setShowRegionModal] = useState(false);
  const [regionInfo, setRegionInfo] = useState<PaymentRegionInfo | null>(null);
  const [regionLoading, setRegionLoading] = useState(false);
  const [pendingPurchase, setPendingPurchase] = useState<{ type: 'membership'; value: string } | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);

  // 二维码弹窗状态
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [qrOrderNo, setQrOrderNo] = useState<string | null>(null);
  const [qrPaymentType, setQrPaymentType] = useState<string | null>(null);

  // 价格地区状态
  const [paymentRegion, setPaymentRegion] = useState<string>('CN');

  const isCofounder = membershipInfo?.isCofounder === true;
  const cofounderDiscount = membershipInfo?.cofounderDiscount || 0;
  const discountMultiplier = isCofounder ? (100 - cofounderDiscount) / 100 : 1;

  const applyDiscount = (price: number | string): string => {
    const num = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(num) || !isCofounder) return String(price);
    return (num * discountMultiplier).toFixed(0);
  };

  // 根据IP地区选择价格和币种
  const regionPricing = paymentRegion === 'OVERSEAS' ? translations.en.pricing : translations.zh.pricing;
  const regionPrices = regionPricing.prices;
  const regionCurrency = regionPricing.currencySymbol;

  useEffect(() => {
    loadMembershipInfo();
    getPaymentRegion().then(info => {
      setPaymentRegion(info.region);
    }).catch(() => {});
  }, []);

  const loadMembershipInfo = async (retries = 2) => {
    try {
      const info = await getMembershipInfo();
      setMembershipInfo(info);
    } catch (err) {
      if (retries > 0) {
        setTimeout(() => loadMembershipInfo(retries - 1), 1000);
      }
    }
  };

  // 点击购买时先检测地区
  const handlePurchaseClick = async (value: string) => {
    setPendingPurchase({ type: 'membership', value });
    setRegionLoading(true);
    setShowRegionModal(true);
    try {
      const info = await getPaymentRegion();
      setRegionInfo(info);
    } catch {
      setRegionInfo(null);
    } finally {
      setRegionLoading(false);
    }
  };

  // 弹窗确认后执行真正的购买
  const handleRegionConfirm = () => {
    if (!selectedPaymentMethod) return;
    setShowRegionModal(false);
    if (!pendingPurchase) return;
    const method = selectedPaymentMethod;
    setSelectedPaymentMethod(null);
    handlePurchaseMembership(pendingPurchase.value as 'pro', method);
    setPendingPurchase(null);
  };

  // 购买会员
  const handlePurchaseMembership = async (membershipType: 'pro', paymentMethod: string) => {
    setPurchasing(membershipType);
    setPurchaseError(null);
    setPurchaseSuccess(null);
    try {
      const months = isAnnual ? 12 : 1;
      const response = await purchaseMembership({ membershipType, months, isAnnual, paymentMethod });

      if (response.codeUrl) {
        setQrCodeUrl(response.codeUrl);
        setQrOrderNo(response.orderNo);
        setQrPaymentType(response.paymentType || null);
        setShowQrModal(true);
      } else if (response.payForm) {
        const div = document.createElement('div');
        div.innerHTML = response.payForm;
        document.body.appendChild(div);
        const form = div.querySelector('form');
        if (form) {
          form.setAttribute('target', '_blank');
          form.submit();
        }
        document.body.removeChild(div);
        setPurchaseSuccess(
          language === 'zh'
            ? '已跳转支付宝支付页面，请在新窗口完成支付'
            : 'Redirected to Alipay payment page, please complete payment in the new window'
        );
        await loadMembershipInfo();
      } else {
        await confirmMembershipPurchase(response.orderNo);
        setPurchaseSuccess(
          language === 'zh'
            ? '购买成功！已开通Pro会员'
            : 'Purchase successful! Pro membership activated'
        );
        await loadMembershipInfo();
      }
    } catch (err: any) {
      setPurchaseError(err.message || (language === 'zh' ? '购买失败' : 'Purchase failed'));
    } finally {
      setPurchasing(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 isolate">
      {/* Blurred Backdrop */}
      <div
        className="absolute inset-0 bg-white/35 backdrop-blur-[8px] transform-gpu pointer-events-auto transition-none"
        style={{ backfaceVisibility: 'hidden', perspective: 1000 }}
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,rgba(255,255,255,0.25)_100%)] pointer-events-none"></div>
      </div>

      {/* Modal Container */}
      <div className="relative w-full max-w-6xl max-h-[90vh] overflow-y-auto bg-white rounded-pricing shadow-modal-heavy border border-white animate-in zoom-in-95 fade-in duration-500 z-10">
        <button
          onClick={onClose}
          className="absolute top-6 right-6 z-20 p-2.5 text-ink-muted hover:text-brand-900 rounded-full transition-colors hover:bg-surface"
        >
          <X size={22} />
        </button>

        <div className="px-8 md:px-12 pt-10 pb-12">
          {/* Welcome Header */}
          <div className="text-center mb-10">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-brand-900 mb-3 tracking-tight">
              {language === 'zh' ? '欢迎加入 Minutes AI' : 'Welcome to Minutes AI'}
            </h2>
            <p className="text-ink-secondary text-base font-medium opacity-70 max-w-lg mx-auto">
              {language === 'zh' ? '选择适合你的方案，开始高效会议之旅' : 'Choose the plan that fits you and start your productive meeting journey'}
            </p>
          </div>

          {/* Annual/Monthly Toggle */}
          <div className="flex items-center justify-center mb-10">
            <div
              className="bg-surface-border/50 p-1.5 rounded-full border border-surface-border inline-flex relative cursor-pointer min-w-[400px] h-14"
              onClick={() => setIsAnnual(!isAnnual)}
            >
              <div
                className={`absolute top-1.5 bottom-1.5 rounded-full transition-all duration-500 ease-[cubic-bezier(0.175,0.885,0.32,1.275)] bg-brand-900 shadow-lg
                ${isAnnual ? 'left-[calc(35%+6px)] right-1.5' : 'left-1.5 right-[calc(65%+6px)]'}`}
              ></div>
              <div className={`w-[35%] flex items-center justify-center text-[11px] font-bold relative z-10 transition-colors duration-300 uppercase tracking-widest ${!isAnnual ? 'text-white' : 'text-ink-dark'}`}>
                {t.pricing.monthly}
              </div>
              <div className={`w-[65%] flex items-center justify-center gap-3 text-[11px] font-bold relative z-10 transition-colors duration-300 uppercase tracking-widest ${isAnnual ? 'text-white' : 'text-ink-dark'}`}>
                {t.pricing.yearly}
                <span className={`text-[10px] px-3 py-1 rounded-lg font-black transition-all duration-500 border-2 shadow-sm
                  ${isAnnual
                    ? 'bg-brand-300 text-brand-900 border-white/20 shadow-[0_0_20px_rgba(190,242,100,0.4)] scale-105'
                    : 'bg-white text-brand-600 border-brand-300'}`}>
                  -20% OFF
                </span>
              </div>
            </div>
          </div>

          {/* Purchase Status Toast */}
          {(purchaseError || purchaseSuccess) && (
            <div className="fixed top-4 right-4 z-[60] max-w-md animate-in slide-in-from-top-2 duration-300">
              {purchaseError && (
                <div className="bg-red-50 border border-red-200 rounded-2xl p-4 shadow-lg flex items-start gap-3">
                  <div className="flex-1">
                    <div className="text-sm font-bold text-red-700">{language === 'zh' ? '购买失败' : 'Purchase Failed'}</div>
                    <div className="text-xs text-red-600 mt-1">{purchaseError}</div>
                  </div>
                  <button onClick={() => setPurchaseError(null)} className="text-red-400 hover:text-red-600">×</button>
                </div>
              )}
              {purchaseSuccess && (
                <div className="bg-green-50 border border-green-200 rounded-2xl p-4 shadow-lg flex items-start gap-3">
                  <Check size={20} className="text-green-500 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-bold text-green-700">{language === 'zh' ? '购买成功' : 'Purchase Successful'}</div>
                    <div className="text-xs text-green-600 mt-1">{purchaseSuccess}</div>
                  </div>
                  <button onClick={() => setPurchaseSuccess(null)} className="text-green-400 hover:text-green-600">×</button>
                </div>
              )}
            </div>
          )}

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto items-stretch">

            {/* Free Card */}
            <div className="group p-8 rounded-pricing bg-white border border-surface-border flex flex-col transition-all duration-500 hover:shadow-card-hover">
              <div className="h-[100px] flex flex-col justify-start">
                <div className="text-[11px] font-bold text-ink-muted uppercase tracking-[0.2em] mb-3">ESSENTIAL</div>
                <h3 className="text-3xl font-serif font-bold text-brand-900 mb-2">{t.pricing.plans.basic.name}</h3>
                <p className="text-ink-secondary text-sm leading-relaxed opacity-60 font-medium">{t.pricing.plans.basic.desc}</p>
              </div>
              <div className="h-[80px] mb-4 flex flex-col justify-start">
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-serif text-brand-900 opacity-40">{regionCurrency}</span>
                  <span className="text-5xl font-serif text-brand-900 font-bold">{regionPrices.free}</span>
                  <span className="text-ink-muted text-xs font-bold uppercase tracking-widest ml-1">/mo</span>
                </div>
              </div>
              <div className="flex-1 mb-6">
                <ul className="space-y-3">
                  {t.pricing.features.basic.map((feature, i) => (
                    <li key={i} className="flex gap-3 items-start text-sm font-medium text-ink-secondary">
                      <div className="mt-0.5 w-5 h-5 rounded-full bg-surface flex items-center justify-center text-ink-muted group-hover:bg-surface-border group-hover:text-brand-900 transition-colors shrink-0">
                        <Check size={12} strokeWidth={3} />
                      </div>
                      <span className="line-clamp-2">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-auto">
                <button
                  onClick={onClose}
                  className="w-full h-14 text-brand-900 font-bold text-sm bg-surface rounded-2xl border border-surface-border hover:bg-white hover:shadow-md transition-all active:scale-[0.98]"
                >
                  {language === 'zh' ? '开始使用' : 'Get Started'}
                </button>
              </div>
            </div>

            {/* Pro Card */}
            <div className="group relative p-8 rounded-pricing bg-brand-900 flex flex-col transition-all duration-500 hover:shadow-[0_40px_80px_-20px_rgba(26,46,5,0.4)] ring-1 ring-white/10 overflow-visible">
              <DraggablePopularTag label={t.pricing.mostPopular} className="top-6 right-8" />
              <div className="h-[100px] flex flex-col justify-start">
                <div className="text-[11px] font-bold text-brand-300/60 uppercase tracking-[0.2em] mb-3">PROFESSIONAL</div>
                <h3 className="text-3xl font-serif font-bold text-white mb-2">{t.pricing.plans.pro.name}</h3>
                <p className="text-white/50 text-sm leading-relaxed font-medium">{t.pricing.plans.pro.desc}</p>
              </div>
              <div className="h-[80px] mb-4 flex flex-col justify-start">
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-serif text-white/40">{regionCurrency}</span>
                  {isCofounder ? (
                    <>
                      <span className="text-5xl font-serif text-white font-bold">
                        {applyDiscount(isAnnual ? regionPrices.pro.yearly : regionPrices.pro.monthly)}
                      </span>
                      <span className="text-lg font-serif text-white/30 line-through ml-1">
                        {isAnnual ? regionPrices.pro.yearly : regionPrices.pro.monthly}
                      </span>
                    </>
                  ) : (
                    <span className="text-5xl font-serif text-white font-bold">
                      {isAnnual ? regionPrices.pro.yearly : regionPrices.pro.monthly}
                    </span>
                  )}
                  <span className="text-white/30 text-xs font-bold uppercase tracking-widest ml-1">/mo</span>
                </div>
                {isCofounder && (
                  <span className="inline-block mt-2 text-[9px] px-1.5 py-0.5 rounded bg-brand-300/20 text-brand-300 font-black uppercase tracking-wider border border-brand-300/30">
                    Co-founder -{cofounderDiscount}%
                  </span>
                )}
              </div>
              <div className="flex-1 mb-6">
                <ul className="space-y-3">
                  {t.pricing.features.pro.map((feature, i) => (
                    <li key={i} className="flex gap-3 items-start text-sm font-medium text-white/70">
                      <div className="mt-0.5 w-5 h-5 rounded-full bg-white/10 flex items-center justify-center text-brand-300 group-hover:bg-brand-300/20 transition-colors shrink-0">
                        <Check size={12} strokeWidth={3} />
                      </div>
                      <span className="line-clamp-2">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-auto">
                <button
                  onClick={() => handlePurchaseClick('pro')}
                  disabled={purchasing === 'pro'}
                  className="w-full h-14 bg-brand-300 text-brand-900 font-bold text-sm rounded-2xl hover:bg-brand-glow hover:shadow-[0_0_30px_rgba(190,242,100,0.3)] transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {purchasing === 'pro' ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <>
                      {language === 'zh' ? '立即升级' : 'Upgrade Now'}
                      <ArrowRight size={16} />
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Team Card */}
            <div className="group p-8 rounded-pricing bg-gradient-to-br from-surface-light to-prestige-100 border border-surface-border flex flex-col transition-all duration-500 hover:shadow-card-hover relative overflow-hidden">
              <div className="absolute top-[-30px] right-[-30px] w-40 h-40 bg-prestige-200/40 blur-[40px] rounded-full"></div>
              <div className="h-[100px] flex flex-col justify-start relative z-10">
                <div className="text-[11px] font-bold text-prestige-300 uppercase tracking-[0.2em] mb-3">ENTERPRISE</div>
                <h3 className="text-3xl font-serif font-bold text-brand-900 mb-2">{t.pricing.plans.team.name}</h3>
                <p className="text-ink-secondary text-sm leading-relaxed opacity-60 font-medium">{t.pricing.plans.team.desc}</p>
              </div>
              <div className="h-[80px] mb-4 flex flex-col justify-center relative z-10">
                <div className="text-lg font-serif font-bold text-ink-secondary">
                  {language === 'zh' ? '专属定制方案' : 'Custom Enterprise Plan'}
                </div>
                <p className="text-xs text-ink-muted mt-1">
                  {language === 'zh' ? '根据团队需求量身定制' : 'Tailored to your team needs'}
                </p>
              </div>
              <div className="flex-1 mb-6 relative z-10">
                <ul className="space-y-3">
                  {t.pricing.features.team.map((feature, i) => (
                    <li key={i} className="flex gap-3 items-start text-sm font-medium text-ink-secondary">
                      <div className="mt-0.5 w-5 h-5 rounded-full bg-prestige-200/60 flex items-center justify-center text-prestige-300 group-hover:bg-prestige-300/20 transition-colors shrink-0">
                        <Check size={12} strokeWidth={3} />
                      </div>
                      <span className="line-clamp-2">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-auto relative z-10">
                <button
                  onClick={() => setShowContactModal(true)}
                  className="w-full h-14 bg-prestige-900 text-prestige-300 font-bold text-sm rounded-2xl hover:bg-prestige-950 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                >
                  <Mail size={16} />
                  {language === 'zh' ? '联系我们' : 'Contact Us'}
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Team 联系我们弹窗 */}
      {showContactModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowContactModal(false)}
          ></div>
          <div className="relative w-full max-w-md bg-surface-light rounded-modal shadow-2xl border border-surface-border overflow-hidden animate-in zoom-in-95 duration-300">
            <div className="absolute inset-0 bg-noise opacity-[0.03] pointer-events-none"></div>
            <div className="absolute top-0 right-0 w-48 h-48 bg-prestige-300/10 rounded-bl-[120px] pointer-events-none"></div>
            <button
              onClick={() => setShowContactModal(false)}
              className="absolute top-6 right-6 z-20 p-2 text-ink-muted hover:text-brand-900 rounded-full transition-colors hover:bg-surface"
            >
              <X size={20} />
            </button>
            <div className="p-10 relative z-10">
              <div className="w-16 h-16 bg-prestige-900 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Mail size={28} className="text-prestige-300" />
              </div>
              <h3 className="text-2xl font-serif font-bold text-brand-900 text-center mb-2">
                {language === 'zh' ? '联系我们' : 'Contact Us'}
              </h3>
              <p className="text-sm text-ink-secondary text-center mb-8 leading-relaxed">
                {language === 'zh'
                  ? 'Team 方案需要专属定制，请通过邮件联系我们，我们将为您提供专属服务。'
                  : 'Team plans require custom setup. Please contact us via email for dedicated service.'}
              </p>
              <div className="bg-surface rounded-2xl border border-surface-border p-5 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 bg-white rounded-xl border border-surface-border flex items-center justify-center shrink-0 shadow-sm">
                    <Mail size={18} className="text-ink-secondary" />
                  </div>
                  <span className="text-sm font-mono font-bold text-brand-900 truncate">support@minutes-ai.com</span>
                </div>
                <button
                  onClick={() => {
                    copyToClipboard('support@minutes-ai.com');
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  className={`shrink-0 px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${
                    copied ? 'bg-brand-500 text-white' : 'bg-brand-900 text-white hover:bg-brand-800'
                  }`}
                >
                  {copied ? (language === 'zh' ? '已复制' : 'Copied') : (language === 'zh' ? '复制' : 'Copy')}
                </button>
              </div>
              <button
                onClick={() => setShowContactModal(false)}
                className="w-full mt-6 py-3 rounded-xl text-sm font-bold text-ink-secondary bg-white border border-surface-border hover:bg-surface transition-all"
              >
                {language === 'zh' ? '关闭' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 地区检测 + 支付方式选择弹窗 */}
      {showRegionModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => { setShowRegionModal(false); setPendingPurchase(null); setSelectedPaymentMethod(null); }}
          ></div>
          <div className="relative w-full max-w-md bg-surface-light rounded-modal shadow-2xl border border-surface-border overflow-hidden animate-in zoom-in-95 duration-300">
            <button
              onClick={() => { setShowRegionModal(false); setPendingPurchase(null); setSelectedPaymentMethod(null); }}
              className="absolute top-6 right-6 z-20 p-2 text-ink-muted hover:text-brand-900 rounded-full transition-colors hover:bg-surface"
            >
              <X size={20} />
            </button>
            <div className="p-10">
              <div className="w-16 h-16 bg-brand-50 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Globe size={28} className="text-brand-700" />
              </div>
              <h3 className="text-2xl font-serif font-bold text-brand-900 text-center mb-2">
                {language === 'zh' ? '选择支付方式' : 'Choose Payment Method'}
              </h3>
              {regionLoading ? (
                <div className="flex flex-col items-center gap-3 py-8">
                  <Loader2 size={32} className="animate-spin text-brand-700" />
                  <p className="text-sm text-ink-secondary">{language === 'zh' ? '正在检测您的地区...' : 'Detecting your region...'}</p>
                </div>
              ) : regionInfo ? (
                <div className="space-y-4">
                  <div className="bg-surface rounded-2xl border border-surface-border p-4">
                    <div className="flex items-center gap-2 text-xs text-ink-secondary">
                      <MapPin size={14} className="text-brand-700" />
                      <span>{regionInfo.region === 'CN' ? (language === 'zh' ? '国内' : 'Mainland China') : (language === 'zh' ? '国外' : 'Overseas')}</span>
                      {regionInfo.city && <span>· {regionInfo.city}</span>}
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="text-xs font-bold text-brand-700 mb-1">
                      {language === 'zh' ? '请选择支付方式' : 'Select payment method'}
                    </div>
                    {regionInfo.availablePaymentMethods.filter(m => m === 'wechat' || m === 'alipay').map((m) => (
                      <button
                        key={m}
                        onClick={() => setSelectedPaymentMethod(m)}
                        className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all ${
                          selectedPaymentMethod === m
                            ? 'border-brand-700 bg-brand-50'
                            : 'border-surface-border bg-white hover:border-brand-200'
                        }`}
                      >
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg font-bold ${
                          m === 'wechat' ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'
                        }`}>
                          {m === 'wechat' ? '微' : '支'}
                        </div>
                        <div className="text-left">
                          <div className="text-sm font-bold text-brand-900">
                            {m === 'wechat' ? (language === 'zh' ? '微信支付' : 'WeChat Pay') : (language === 'zh' ? '支付宝' : 'Alipay')}
                          </div>
                          <div className="text-xs text-ink-secondary">
                            {m === 'wechat' ? (language === 'zh' ? '使用微信扫码支付' : 'Scan QR code with WeChat') : (language === 'zh' ? '跳转支付宝页面支付' : 'Redirect to Alipay payment page')}
                          </div>
                        </div>
                        <div className="ml-auto">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            selectedPaymentMethod === m ? 'border-brand-700 bg-brand-700' : 'border-ink-border'
                          }`}>
                            {selectedPaymentMethod === m && <Check size={12} className="text-white" />}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 text-sm text-ink-secondary">
                  {language === 'zh' ? '地区检测失败，将使用默认设置' : 'Region detection failed, using defaults'}
                </div>
              )}
              <button
                onClick={handleRegionConfirm}
                disabled={regionLoading || !selectedPaymentMethod}
                className="w-full mt-6 h-12 rounded-xl font-bold text-sm bg-brand-900 text-white hover:bg-brand-800 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {regionLoading
                  ? (language === 'zh' ? '检测中...' : 'Detecting...')
                  : (language === 'zh' ? '确认支付' : 'Confirm Payment')}
              </button>
              <button
                onClick={() => { setShowRegionModal(false); setPendingPurchase(null); setSelectedPaymentMethod(null); }}
                className="w-full mt-3 py-3 rounded-xl text-sm font-bold text-ink-secondary bg-white border border-surface-border hover:bg-surface transition-all"
              >
                {language === 'zh' ? '取消' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 二维码支付弹窗 */}
      {showQrModal && qrCodeUrl && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowQrModal(false)}
          ></div>
          <div className="relative w-full max-w-sm bg-surface-light rounded-modal shadow-2xl border border-surface-border overflow-hidden animate-in zoom-in-95 duration-300">
            <button
              onClick={() => setShowQrModal(false)}
              className="absolute top-6 right-6 z-20 p-2 text-ink-muted hover:text-brand-900 rounded-full transition-colors hover:bg-surface"
            >
              <X size={20} />
            </button>
            <div className="p-10 text-center">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg text-2xl font-bold ${
                qrPaymentType?.includes('wechat') ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'
              }`}>
                {qrPaymentType?.includes('wechat') ? '微' : '支'}
              </div>
              <h3 className="text-xl font-serif font-bold text-brand-900 mb-2">
                {qrPaymentType?.includes('wechat')
                  ? (language === 'zh' ? '微信扫码支付' : 'WeChat Pay')
                  : (language === 'zh' ? '支付宝扫码支付' : 'Alipay')}
              </h3>
              <p className="text-xs text-ink-secondary mb-6">
                {language === 'zh' ? '请使用手机扫描下方二维码完成支付' : 'Scan the QR code below with your phone to pay'}
              </p>
              <div className="bg-white border-2 border-surface-border rounded-2xl p-4 inline-block mb-4">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrCodeUrl)}`}
                  alt="Payment QR Code"
                  className="w-[200px] h-[200px]"
                />
              </div>
              {qrOrderNo && (
                <p className="text-xs text-ink-muted mb-4">
                  {language === 'zh' ? '订单号：' : 'Order: '}{qrOrderNo}
                </p>
              )}
              <p className="text-xs text-ink-secondary">
                {language === 'zh' ? '支付完成后页面将自动更新' : 'Page will update after payment'}
              </p>
              <button
                onClick={async () => {
                  setShowQrModal(false);
                  setQrCodeUrl(null);
                  await loadMembershipInfo();
                }}
                className="w-full mt-6 h-12 rounded-xl font-bold text-sm bg-brand-900 text-white hover:bg-brand-800 transition-all active:scale-[0.98]"
              >
                {language === 'zh' ? '我已完成支付' : 'I have paid'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
